import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { Node, mergeAttributes } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react'
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Maximize2,
  Replace,
  Trash2,
  Type,
} from 'lucide-react'
import { uploadAdminMedia } from '../media'

export type ImageAlign = 'left' | 'center' | 'right' | 'wrap-left' | 'wrap-right'

const WIDTH_PRESETS = [25, 40, 50, 66, 75, 100] as const
const WIDTH_MIN = 20
const WIDTH_MAX = 100

function clampWidth(n: number) {
  if (!Number.isFinite(n)) return 100
  return Math.min(WIDTH_MAX, Math.max(WIDTH_MIN, Math.round(n)))
}

function styleFor(widthPercent: number, align: ImageAlign): string {
  const w = clampWidth(widthPercent)
  const base = `width: ${w}%; max-width: 100%; height: auto;`
  if (align === 'wrap-left') {
    return `${base} display: block; float: left; margin: 0.35rem 1.25rem 0.85rem 0;`
  }
  if (align === 'wrap-right') {
    return `${base} display: block; float: right; margin: 0.35rem 0 0.85rem 1.25rem;`
  }
  if (align === 'left') {
    return `${base} display: block; margin: 0.75rem auto 0.75rem 0; float: none;`
  }
  if (align === 'right') {
    return `${base} display: block; margin: 0.75rem 0 0.75rem auto; float: none;`
  }
  return `${base} display: block; margin: 0.75rem auto; float: none;`
}

function parseAlign(raw: string | null, style: string): ImageAlign {
  if (raw === 'left' || raw === 'right' || raw === 'center' || raw === 'wrap-left' || raw === 'wrap-right') {
    return raw
  }
  const s = style.toLowerCase()
  if (s.includes('float: left') || s.includes('float:left')) return 'wrap-left'
  if (s.includes('float: right') || s.includes('float:right')) return 'wrap-right'
  if (s.includes('margin-left: auto') && s.includes('margin-right: 0')) return 'right'
  if (s.includes('margin-right: auto') && s.includes('margin-left: 0')) return 'left'
  return 'center'
}

function ImageNodeView({ node, updateAttributes, deleteNode, selected }: NodeViewProps) {
  const widthPercent = clampWidth(Number(node.attrs['data-width-percent'] || 100))
  const align = (node.attrs['data-align'] as ImageAlign) || 'center'
  const alt = String(node.attrs.alt || '')
  const src = String(node.attrs.src || '')
  const wrapRef = useRef<HTMLDivElement>(null)
  const replaceRef = useRef<HTMLInputElement>(null)
  const [editingAlt, setEditingAlt] = useState(false)
  const [altDraft, setAltDraft] = useState(alt)
  const [replacing, setReplacing] = useState(false)

  useEffect(() => {
    setAltDraft(alt)
  }, [alt])

  const applyWidth = useCallback(
    (next: number) => {
      const w = clampWidth(next)
      updateAttributes({
        'data-width-percent': String(w),
        style: styleFor(w, align),
      })
    },
    [align, updateAttributes]
  )

  const applyAlign = useCallback(
    (next: ImageAlign) => {
      updateAttributes({
        'data-align': next,
        style: styleFor(widthPercent, next),
      })
    },
    [updateAttributes, widthPercent]
  )

  const onResizePointerDown = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const wrap = wrapRef.current
      if (!wrap) return
      const startX = e.clientX
      const startW = widthPercent
      const parentW = wrap.parentElement?.getBoundingClientRect().width || wrap.getBoundingClientRect().width

      const onMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startX
        const deltaPct = (dx / parentW) * 100 * (align === 'right' || align === 'wrap-right' ? -1 : 1)
        applyWidth(startW + deltaPct)
      }
      const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [align, applyWidth, widthPercent]
  )

  const onReplace = useCallback(
    async (file: File | null | undefined) => {
      if (!file) return
      setReplacing(true)
      try {
        const up = await uploadAdminMedia(file)
        updateAttributes({
          src: up.url,
          'data-cdn-path': up.relativePath || up.storedPath.replace(/^cdn:/, ''),
          alt: alt || file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '),
          style: styleFor(widthPercent, align),
        })
      } catch (err) {
        window.alert(err instanceof Error ? err.message : 'Replace failed')
      } finally {
        setReplacing(false)
        if (replaceRef.current) replaceRef.current.value = ''
      }
    },
    [align, alt, updateAttributes, widthPercent]
  )

  const isFloat = align === 'wrap-left' || align === 'wrap-right'

  return (
    <NodeViewWrapper
      as="div"
      className={`rte-img-wrap rte-img-${align}${selected ? ' is-selected' : ''}${isFloat ? ' is-float' : ''}`}
      data-drag-handle
      style={{
        width: `${widthPercent}%`,
        maxWidth: '100%',
        ...(align === 'wrap-left'
          ? { float: 'left', margin: '0.35rem 1.25rem 0.85rem 0' }
          : align === 'wrap-right'
            ? { float: 'right', margin: '0.35rem 0 0.85rem 1.25rem' }
            : {}),
      }}
    >
      <div ref={wrapRef} className="rte-img-inner relative">
        <input
          ref={replaceRef}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(e) => void onReplace(e.target.files?.[0])}
        />
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="rte-img"
          data-cdn-path={node.attrs['data-cdn-path'] || undefined}
          data-width-percent={widthPercent}
          data-align={align}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />

        {selected && (
          <>
            <div className="rte-img-toolbar" contentEditable={false}>
              <button type="button" title="Block left" className={align === 'left' ? 'on' : ''} onClick={() => applyAlign('left')}>
                <AlignLeft className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                title="Center"
                className={align === 'center' ? 'on' : ''}
                onClick={() => applyAlign('center')}
              >
                <AlignCenter className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                title="Block right"
                className={align === 'right' ? 'on' : ''}
                onClick={() => applyAlign('right')}
              >
                <AlignRight className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                title="Wrap text left"
                className={align === 'wrap-left' ? 'on' : ''}
                onClick={() => applyAlign('wrap-left')}
              >
                ⫷
              </button>
              <button
                type="button"
                title="Wrap text right"
                className={align === 'wrap-right' ? 'on' : ''}
                onClick={() => applyAlign('wrap-right')}
              >
                ⫸
              </button>
              <span className="rte-sep" />
              {WIDTH_PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  title={`${p}% width`}
                  className={widthPercent === p ? 'on' : ''}
                  onClick={() => applyWidth(p)}
                >
                  {p}%
                </button>
              ))}
              <span className="rte-sep" />
              <button
                type="button"
                title="Edit alt text (SEO)"
                className={editingAlt ? 'on' : ''}
                onClick={() => setEditingAlt((v) => !v)}
              >
                <Type className="h-3.5 w-3.5" />
              </button>
              <button type="button" title="Full width" onClick={() => applyWidth(100)}>
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                title={replacing ? 'Replacing…' : 'Replace image'}
                disabled={replacing}
                onClick={() => replaceRef.current?.click()}
              >
                <Replace className="h-3.5 w-3.5" />
              </button>
              <button type="button" title="Delete image" className="danger" onClick={() => deleteNode()}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>

            {editingAlt && (
              <div className="rte-img-alt" contentEditable={false}>
                <input
                  value={altDraft}
                  onChange={(e) => setAltDraft(e.target.value)}
                  placeholder="Alt text for SEO / accessibility"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      updateAttributes({ alt: altDraft.trim() })
                      setEditingAlt(false)
                    }
                    if (e.key === 'Escape') {
                      setAltDraft(alt)
                      setEditingAlt(false)
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    updateAttributes({ alt: altDraft.trim() })
                    setEditingAlt(false)
                  }}
                >
                  Save
                </button>
              </div>
            )}

            <span className="rte-img-badge" contentEditable={false}>
              {widthPercent}% · {align}
            </span>

            <button
              type="button"
              className="rte-resize-handle"
              title="Drag to resize"
              contentEditable={false}
              onMouseDown={onResizePointerDown}
            />
          </>
        )}
      </div>
    </NodeViewWrapper>
  )
}

export const ResizableImage = Node.create({
  name: 'image',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
      'data-cdn-path': {
        default: null,
        parseHTML: (el) => el.getAttribute('data-cdn-path'),
        renderHTML: (attrs) => (attrs['data-cdn-path'] ? { 'data-cdn-path': attrs['data-cdn-path'] } : {}),
      },
      'data-width-percent': {
        default: '100',
        parseHTML: (el) => {
          const attr = el.getAttribute('data-width-percent')
          if (attr) return String(clampWidth(Number(attr)))
          const style = el.getAttribute('style') || ''
          const m = /width\s*:\s*(\d+(?:\.\d+)?)\s*%/i.exec(style)
          return String(clampWidth(m ? Number(m[1]) : 100))
        },
        renderHTML: (attrs) => ({
          'data-width-percent': String(clampWidth(Number(attrs['data-width-percent'] || 100))),
        }),
      },
      'data-align': {
        default: 'center',
        parseHTML: (el) => parseAlign(el.getAttribute('data-align'), el.getAttribute('style') || ''),
        renderHTML: (attrs) => ({
          'data-align': attrs['data-align'] || 'center',
        }),
      },
      style: {
        default: null,
        parseHTML: (el) => el.getAttribute('style'),
        renderHTML: (attrs) => {
          const w = clampWidth(Number(attrs['data-width-percent'] || 100))
          const align = (attrs['data-align'] as ImageAlign) || 'center'
          return { style: styleFor(w, align) }
        },
      },
      class: {
        default: 'rounded-xl max-w-full h-auto',
      },
    }
  },

  parseHTML() {
    return [{ tag: 'img[src]' }]
  },

  renderHTML({ HTMLAttributes }) {
    const w = clampWidth(Number(HTMLAttributes['data-width-percent'] || 100))
    const align = (HTMLAttributes['data-align'] as ImageAlign) || 'center'
    return [
      'img',
      mergeAttributes(HTMLAttributes, {
        'data-width-percent': String(w),
        'data-align': align,
        style: styleFor(w, align),
        class: 'rounded-xl max-w-full h-auto',
      }),
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView)
  },
})
